import * as RadixPortal from '@radix-ui/react-portal'
import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { CommentsThread } from '@root/features/comment/ui'
import {
  PairInformation,
  PairInformationSkeleton,
} from '@root/features/pair/ui'
import {
  tradeSwapTypeAtom,
  tradingPairAddressAtom,
  toggleTradingAuditAction,
} from '@root/features/trade/trading'
import { tradingPairStateAtom } from '@root/features/trade/trading/atoms/reatom-trading-pair'
import {
  reatomBuyControls,
  reatomSellControls,
} from '@root/features/trade/trading-controls/atoms/common'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage } from '@root/shared/ui-kit'
import { layoutHeightCss } from '@root/shared/ui-kit/utils/page-layout'
import { InstantTradeDialogs } from '@root/widgets/instant-trade-dialog/ui/instant-trade-dialogs'

import { handleBackAction } from './atoms'
import { reatomTradesTable } from './atoms/reatom-trades-table'
import { tradeTableTabAtom } from './atoms/reatom-trading-tables'
import { TradePageChart } from './trade-page-chart'
import { PairMetadata } from './ui/pair-metadata'
import { TradeArea } from './ui/trade-area/trade-area'
import { TradeTables } from './ui/trade-tables/trade-tables'

import type { ClassNameProps } from '@root/shared/react'

type TradePageProps = {} & ClassNameProps

export const TradePage_: React.FC<TradePageProps> = ({ className }) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const commentRef = useRef<HTMLDivElement>(null)
  const [, setTradeTableTab] = useAtom(tradeTableTabAtom)

  const [state] = useAtom(tradingPairStateAtom)
  const [pairAddress] = useAtom(tradingPairAddressAtom)
  const toggleShowAudit = useAction(toggleTradingAuditAction)
  const [__, setTradeSwapType] = useAtom(tradeSwapTypeAtom)
  const { t } = useTranslation()
  const navigate = useNavigate()

  // eslint-disable-next-line @reatom/unit-naming-rule
  const buyControlsModel = useMemo(() => reatomBuyControls(), [])
  // eslint-disable-next-line @reatom/unit-naming-rule
  const sellControlsModel = useMemo(() => reatomSellControls(), [])

  const handleBack = useAction(handleBackAction)

  useEffect(() => {
    setTradeSwapType('buy')
  }, [pairAddress, setTradeSwapType])

  const tradesTableModel = useMemo(
    () =>
      AsyncStateN.isSuccess(state)
        ? // eslint-disable-next-line @reatom/unit-naming-rule
          reatomTradesTable({ pair: state.value })
        : undefined,
    [state],
  )
  const ctx = useCtx()

  useEffect(() => {
    if (tradesTableModel) {
      const dispose = tradesTableModel.initialize(ctx)

      return () => {
        dispose()
      }
    }
  }, [tradesTableModel, ctx])

  if (AsyncStateN.isError(state) && state.error._tag === 'PairNotFoundError') {
    return (
      <ErrorContainer>
        <InfoMessage
          variant="dt"
          intent="error"
          title={t('pair.pair-not-found')}
          description={t('pair.pair-not-found.description')}
        >
          <BackButton
            variant="solid"
            radius="rounded"
            color="primary"
            size="xs"
            onClick={() => {
              navigate('/')
            }}
          >
            {t('common.back')}
          </BackButton>
        </InfoMessage>
      </ErrorContainer>
    )
  }

  return (
    <PageRoot className={className}>
      <Top>
        <span ref={chartRef} />
        {AsyncStateN.isSuccess(state) ? (
          <>
            <PairMetadata pair={state.value} />
            <PairInformation
              pair={state.value}
              onAuditClick={toggleShowAudit}
              onBack={handleBack}
              onDevTokensClick={() => {
                setTradeTableTab('dev-tokens')
              }}
            />
          </>
        ) : (
          <PairInformationSkeleton />
        )}
        <TradePageChart
          tradesTableModel={tradesTableModel}
          buyControlsModel={buyControlsModel}
          sellControlsModel={sellControlsModel}
        />
      </Top>
      <Divider />
      <TradeTables
        pair={state}
        pairAddress={pairAddress}
        tradesTableModel={tradesTableModel}
      />
      {AsyncStateN.isSuccess(state) ? (
        <CommentsThread ref={commentRef} pair={state.value} />
      ) : undefined}
      <TradeArea
        buyControlsModel={buyControlsModel}
        sellControlsModel={sellControlsModel}
        pairState={state}
      />
      <RadixPortal.Root>
        {AsyncStateN.isSuccess(state) ? (
          <InstantTradeDialogs pair={state.value} />
        ) : undefined}
      </RadixPortal.Root>
    </PageRoot>
  )
}

export const TradePage: React.FC<TradePageProps> = ({ className }) => {
  const [pairAddress] = useAtom(tradingPairAddressAtom)
  // TODO: This is to keep subscription to pair so it can be loaded when switching between pairs. Revise
  useAtom(tradingPairStateAtom)
  return <TradePage_ key={pairAddress} className={className} />
}

const Root = styled.div`
  height: 100%;
  min-height: 0;

  background: var(--appearance-1);
  background: var(--appearance-1);
`

const PageRoot = styled.div`
  min-height: 0;
  min-width: 0;
  /* Stable min height for sticky trade column; cqh = desktop MainScroll scrollport */
  min-height: 100vh;
  min-height: 100cqh;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto auto 500px 600px;

  background: var(--appearance-1);
  background: var(--appearance-1);
`

const Top = styled.div`
  --pair-ticker-font-size: 16px;
  --pair-ticker-gap: 4px;

  display: grid;
`

const Divider = styled.div``

const ErrorContainer = styled(Root)`
  ${layoutHeightCss}

  display: grid;
  place-content: center;
`

const BackButton = styled(Button)`
  margin-top: 16px;
`
